import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { useUserOrg } from '../lib/org';
import { useSession } from '../lib/auth';
import {
  listSites,
  createRams,
  type Site,
} from '../lib/db';
import { uploadRams, getSignedUrl } from '../lib/storage';
import { RAMS_TEMPLATES, type RamsTemplate } from '../data/ramsTemplates';
import { ramsTradesForOrg } from '../lib/features';
import { generateRamsTemplatePdf } from '../lib/pdf';
import { LoadingIndicator } from '../components/LoadingIndicator';

const FIRST_RAMS_DONE_KEY = 'hs_first_rams_done';

/**
 * First-RAMS onboarding flow.
 *
 * Lands here right after the Welcome wizard finishes. The goal: get the
 * user from "I just signed up" to "a branded RAMS PDF is in my inbox" in
 * under five minutes, so the first thing they see in the product is the
 * exact use-case they paid for.
 *
 * What we already know from the Welcome wizard:
 *   - Their org details + logo
 *   - Their primary trade (from profile picker step 4)
 *   - Their first site (from site picker step 5)
 *   - Their H&S policy is signed (step 3)
 *
 * What we do here:
 *   1. Pick the RAMS template that fits their trade (see
 *      PROFILE_TRADE_TO_DEFAULT_RAMS_TEMPLATE in features.ts).
 *   2. Pre-tick every hazard + PPE item the template provides — the user
 *      just scrolls and unticks anything that doesn't apply on their
 *      site. Tick-first UI throughout.
 *   3. One tap to Generate. PDF is produced, uploaded to the RAMS
 *      library against the first site, and the user lands on a
 *      celebration screen with a "Open my RAMS" button.
 *
 * If anything fails or the user skips, we still set the
 * hs_first_rams_done flag so they don't get nagged again — they can
 * always make a "proper" RAMS from the regular flow later.
 */
export function WelcomeFirstRams() {
  const nav = useNavigate();
  const { org } = useUserOrg();
  const { session } = useSession();

  // Step state — pick a template first, then tick the form.
  const [step, setStep] = useState<'pick' | 'tick'>('pick');
  const [templateId, setTemplateId] = useState<string | null>(null);
  const template: RamsTemplate | undefined = templateId
    ? RAMS_TEMPLATES.find((t) => t.id === templateId)
    : undefined;

  // Trade filter chip — defaults to null (all trades). Same UX as
  // /sites/:siteId/rams/presets so the user has a single mental model
  // of "how RAMS templates work" across the app.
  const [activeTrade, setActiveTrade] = useState<string | null>(null);

  // Templates relevant to the user's profile — used to surface a
  // "★ For your trade" filter chip at the top.
  const forYouTrades = org ? ramsTradesForOrg(org.trades, org.risky_activities) : [];
  const forYouTemplates = forYouTrades.length === 0
    ? []
    : RAMS_TEMPLATES.filter((t) => t.trades.some((tr) => forYouTrades.includes(tr)));
  const [showForYouOnly, setShowForYouOnly] = useState(forYouTemplates.length > 0);

  // All unique trade strings present in the template catalogue.
  const allTrades = Array.from(
    new Set(RAMS_TEMPLATES.flatMap((t) => t.trades)),
  ).sort();

  // Active filter applied to the catalogue.
  const filtered: RamsTemplate[] = (showForYouOnly ? forYouTemplates : RAMS_TEMPLATES)
    .filter((t) => activeTrade ? t.trades.includes(activeTrade) : true);

  // Group by primary trade for the default view (no specific filter).
  const grouped: Record<string, RamsTemplate[]> = {};
  if (!activeTrade) {
    for (const t of filtered) {
      const primary = t.trades[0] ?? 'Other';
      (grouped[primary] = grouped[primary] ?? []).push(t);
    }
  }
  const groupedTrades = Object.keys(grouped).sort();

  const [site, setSite] = useState<Site | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Tick state, seeded from the template with everything ticked. The
  // user unticks anything that doesn't apply.
  const [hazardChecks, setHazardChecks] = useState<boolean[]>([]);
  const [ppeChecks, setPpeChecks] = useState<boolean[]>([]);

  // Celebration state — set when generation succeeds.
  const [done, setDone] = useState<{
    seconds: number;
    fileUrl: string;
    ramsId: string;
    siteId: string;
  } | null>(null);

  // Track how long the user spent on the screen for the celebration
  // counter. Useful brag stat for the marketing site too.
  const startedAt = useRef<number>(Date.now());

  useEffect(() => {
    if (!org) return;
    setLoading(true);
    listSites(org.org_id)
      .then((sites) => {
        // Take the most-recently-created site — that's the one they
        // just made in Welcome step 5.
        if (sites.length > 0) {
          setSite(sites[sites.length - 1]);
        } else {
          setError('No sites yet. Make one in Sites first.');
        }
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [org]);

  useEffect(() => {
    if (!template) return;
    setHazardChecks(template.hazards.map(() => true));
    setPpeChecks(template.ppe.map(() => true));
  }, [template]);

  function toggleHazard(idx: number) {
    setHazardChecks((p) => p.map((v, i) => (i === idx ? !v : v)));
  }
  function togglePpe(idx: number) {
    setPpeChecks((p) => p.map((v, i) => (i === idx ? !v : v)));
  }

  function markFirstRamsDone() {
    try { localStorage.setItem(FIRST_RAMS_DONE_KEY, '1'); } catch { /* noop */ }
  }

  function skip() {
    markFirstRamsDone();
    nav('/');
  }

  async function generate() {
    if (!org || !site || !template) {
      setError('Loading… try again in a moment.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const ramsId = crypto.randomUUID();
      const dateStr = new Date().toLocaleDateString('en-GB', {
        day: '2-digit', month: 'long', year: 'numeric',
      });
      const userName =
        (session?.user.user_metadata?.full_name as string | undefined) ?? org.org_name;

      // Hazards body: only ticked ones, formatted as the PDF parser expects.
      const hazardsText = template.hazards
        .filter((_, i) => hazardChecks[i])
        .map((h) => `[${h.risk}] ${h.hazard}\n   Controls: ${h.controls}`)
        .join('\n\n');

      // PPE: only ticked, newline-separated.
      const ppeText = template.ppe
        .filter((_, i) => ppeChecks[i])
        .join('\n');

      const blob = await generateRamsTemplatePdf({
        orgName: org.org_name,
        orgLogoPath: org.logo_path,
        title: template.title,
        trade: template.trades[0],
        // Review checks all left unticked — the user can sign + tick
        // properly later. Onboarding RAMS is "your first one to look at",
        // not a legally-issued document.
        reviewChecks: [false, false, false, false, false],
        pcSignatureDataUrl: null,
        pcName: userName,
        fields: {
          site_name: site.name,
          plot_or_area: '—',
          client: site.client_name || '—',
          company: org.org_name,
          company_contact: userName,
          company_mobile: '—',
          prepared_by: userName,
          date: dateStr,
          scope: template.scope,
          hazards: hazardsText,
          sequence: template.sequence.map((s, i) => `${i + 1}. ${s}`).join('\n'),
          equipment: template.equipment.join('\n'),
          ppe: ppeText,
          emergency: template.emergency,
          notes: 'Generated during onboarding. Open it, review the content, sign it properly before issuing on site.',
        },
      });

      const fileName = `${template.id}-first-${dateStr.replace(/\s+/g, '-')}.pdf`;
      const file = new File([blob], fileName, { type: 'application/pdf' });
      const filePath = await uploadRams(org.org_id, ramsId, file);

      await createRams({
        id: ramsId,
        orgId: org.org_id,
        siteId: site.id,
        title: `${template.title} — first RAMS`,
        company: org.org_name,
        trade_preset: template.trades[0],
        task_preset: null,
        hazard_presets: template.defaultHazards,
        notes: 'Made during onboarding. Edit to fit your site before issuing.',
        file_url: filePath,
        file_name: fileName,
        mime_type: 'application/pdf',
        size_bytes: file.size,
      });

      const fileUrl = await getSignedUrl('rams-files', filePath, 3600);

      const seconds = Math.max(1, Math.round((Date.now() - startedAt.current) / 1000));
      markFirstRamsDone();
      setDone({ seconds, fileUrl, ramsId, siteId: site.id });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSubmitting(false);
    }
  }

  if (loading) {
    return <AppShell title="Your first RAMS"><LoadingIndicator /></AppShell>;
  }

  if (!site) {
    return (
      <AppShell title="Your first RAMS">
        <div className="banner banner-error">{error ?? 'Setup is missing something — try again from the dashboard.'}</div>
        <button type="button" className="btn btn-ghost" onClick={() => nav('/')} style={{ marginTop: 12 }}>
          Skip to dashboard
        </button>
      </AppShell>
    );
  }

  // ----- Celebration screen ------------------------------------------------
  if (done) {
    const mm = Math.floor(done.seconds / 60);
    const ss = (done.seconds % 60).toString().padStart(2, '0');
    return (
      <AppShell title="Done">
        <div style={{ textAlign: 'center', padding: '32px 0 16px' }}>
          <div
            aria-hidden
            style={{
              width: 72, height: 72, borderRadius: '50%',
              background: 'var(--success, #16A34A)', color: '#fff',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 38, fontWeight: 500, marginBottom: 16,
            }}
          >✓</div>
          <h2 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 500 }}>Done in {mm}:{ss}</h2>
          <p className="muted" style={{ margin: '0 auto 18px', maxWidth: 380, fontSize: 14 }}>
            Your first RAMS is in your library, with your logo and your details baked in.
            Open it and have a look — that's what your clients see.
          </p>
        </div>

        <div className="btn-stack">
          <a
            href={done.fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary"
            style={{ textDecoration: 'none', textAlign: 'center' }}
          >
            View my RAMS →
          </a>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => nav(`/sites/${done.siteId}/rams/list?share=${done.ramsId}`)}
          >
            Email a copy to myself
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => nav('/')}
          >
            Just save it in the app
          </button>
        </div>

        <div className="card" style={{ marginTop: 24, background: 'var(--brand-pale)' }}>
          <p style={{ margin: 0, fontSize: 13 }}>
            <strong>Next steps when you've got 10 minutes:</strong> open the RAMS,
            sign it properly with the signature pad, and tick the pre-issue review
            checks. That makes it a properly issued document — ready to send to a
            client or Principal Contractor.
          </p>
        </div>
      </AppShell>
    );
  }

  // ----- Step 1: Pick a template -------------------------------------------
  if (step === 'pick' || !template) {
    function pick(id: string) {
      setTemplateId(id);
      setStep('tick');
    }
    return (
      <AppShell title="Your first RAMS">
        {error && <div className="banner banner-error" style={{ marginBottom: 12 }}>{error}</div>}

        <div className="card" style={{ marginBottom: 12 }}>
          <p style={{ margin: 0, fontWeight: 500 }}>Pick a template for your first RAMS.</p>
          <p className="muted" style={{ margin: '6px 0 0', fontSize: 13 }}>
            Browse by trade. Tap one to use it — you'll untick anything that
            doesn't apply on your site before generating the PDF.
          </p>
          <p className="muted" style={{ margin: '8px 0 0', fontSize: 12 }}>
            Site: <strong>{site.name}</strong>
          </p>
        </div>

        {forYouTemplates.length > 0 && (
          <div className="rams-trade-chips" style={{ marginBottom: 4 }}>
            <button type="button"
              className={`rams-trade-chip${showForYouOnly ? ' active' : ''}`}
              onClick={() => { setShowForYouOnly((v) => !v); setActiveTrade(null); }}>
              ★ For your trade ({forYouTemplates.length})
            </button>
          </div>
        )}

        <div className="rams-trade-chips" role="tablist" aria-label="Filter by trade">
          <button
            type="button"
            className={`rams-trade-chip ${activeTrade === null ? 'active' : ''}`}
            onClick={() => setActiveTrade(null)}
          >
            All trades
          </button>
          {allTrades.map((t) => (
            <button
              key={t}
              type="button"
              className={`rams-trade-chip ${activeTrade === t ? 'active' : ''}`}
              onClick={() => setActiveTrade(activeTrade === t ? null : t)}
            >
              {t}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', marginTop: 12 }}>
            <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
              No templates match. Try a different trade chip above.
            </p>
          </div>
        ) : !activeTrade ? (
          <>
            {groupedTrades.map((trade) => (
              <div key={trade} style={{ marginBottom: 18 }}>
                <h3 className="rams-company-h">{trade}</h3>
                <ul className="rams-preset-list">
                  {grouped[trade].map((t) => (
                    <li key={t.id}>
                      <button
                        type="button"
                        className="rams-preset-row"
                        onClick={() => pick(t.id)}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          background: 'transparent',
                          border: '1px solid var(--border)',
                          cursor: 'pointer',
                        }}
                      >
                        <div className="rams-preset-text">
                          <p className="rams-preset-title">{t.title}</p>
                          <p className="rams-preset-desc">{t.description}</p>
                        </div>
                        <span className="chev">›</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </>
        ) : (
          <ul className="rams-preset-list" style={{ marginTop: 12 }}>
            {filtered.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  className="rams-preset-row"
                  onClick={() => pick(t.id)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    background: 'transparent',
                    border: '1px solid var(--border)',
                    cursor: 'pointer',
                  }}
                >
                  <div className="rams-preset-text">
                    <p className="rams-preset-title">{t.title}</p>
                    <p className="rams-preset-desc">{t.description}</p>
                  </div>
                  <span className="chev">›</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="btn-stack" style={{ marginTop: 18 }}>
          <button type="button" className="btn btn-ghost" onClick={skip}>
            Skip for now — take me to the dashboard
          </button>
        </div>
      </AppShell>
    );
  }

  // ----- Step 2: Tick-and-generate form ------------------------------------
  const hazardsTicked = hazardChecks.filter(Boolean).length;
  const ppeTicked = ppeChecks.filter(Boolean).length;

  return (
    <AppShell title="Your first RAMS">
      {error && <div className="banner banner-error" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="card" style={{ marginBottom: 12 }}>
        <p style={{ margin: 0, fontWeight: 500 }}>Right — let's tailor this RAMS to your site.</p>
        <p className="muted" style={{ margin: '6px 0 0', fontSize: 13 }}>
          Tick what applies, hit Generate, and you'll have a branded PDF in
          your library inside two minutes.
        </p>
      </div>

      <div className="card" style={{ marginBottom: 16, background: 'var(--brand-pale)', borderColor: 'var(--brand-light)' }}>
        <p style={{ margin: 0, fontSize: 12, color: 'var(--brand-deep)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500 }}>
          Chosen template
        </p>
        <p style={{ margin: '4px 0 0', fontWeight: 500 }}>{template.title}</p>
        <p className="muted" style={{ margin: '6px 0 0', fontSize: 13 }}>{template.description}</p>
        <p className="muted" style={{ margin: '8px 0 0', fontSize: 12 }}>
          Site: <strong>{site.name}</strong>
        </p>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => { setStep('pick'); setTemplateId(null); }}
          style={{ marginTop: 10, padding: '6px 10px' }}
        >
          ← Pick a different template
        </button>
      </div>

      <h3 className="section-title">Hazards — tick what applies</h3>
      <p className="muted" style={{ margin: '0 0 8px', fontSize: 13 }}>
        {hazardsTicked} of {template.hazards.length} ticked. Untick anything not on this site.
      </p>
      <div className="check-list" style={{ marginBottom: 20 }}>
        {template.hazards.map((h, i) => (
          <label key={i} className="check-row">
            <input
              type="checkbox"
              checked={hazardChecks[i] ?? false}
              onChange={() => toggleHazard(i)}
            />
            <span style={{ flex: 1 }}>
              <strong>{h.hazard}</strong>
              <span className="muted" style={{ display: 'block', fontSize: 12, marginTop: 2 }}>{h.risk} risk</span>
            </span>
          </label>
        ))}
      </div>

      <h3 className="section-title">PPE — tick what's required</h3>
      <p className="muted" style={{ margin: '0 0 8px', fontSize: 13 }}>
        {ppeTicked} of {template.ppe.length} ticked.
      </p>
      <div className="check-list" style={{ marginBottom: 20 }}>
        {template.ppe.map((p, i) => (
          <label key={i} className="check-row">
            <input
              type="checkbox"
              checked={ppeChecks[i] ?? false}
              onChange={() => togglePpe(i)}
            />
            <span style={{ flex: 1 }}>{p}</span>
          </label>
        ))}
      </div>

      <div className="btn-stack">
        <button
          type="button"
          className="btn btn-primary"
          onClick={generate}
          disabled={submitting}
        >
          {submitting ? 'Generating…' : 'Make my first RAMS →'}
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={skip}
          disabled={submitting}
        >
          Skip for now
        </button>
      </div>
    </AppShell>
  );
}
